import { takeLatest, call, put, select } from 'redux-saga/effects';
import _ from 'lodash';
import { destroy, stopSubmit } from 'redux-form';
import moment from 'moment';
import { updateProject } from '../../../services/projects';
import { transformStatusToBackend } from '../../../utilities/statusUtils';
import { openSnackbar } from '../../_shared/snack-bar/snackbarActions';
import { errorOpenModal } from '../../_shared/modal/errorModalActions';
import { migrationOpenModal } from '../../_shared/modal/migrationModalActions';
import { selectUserRoles } from '../../login/loginSelector';
import { getMaxWeight, plAdminWeight } from '../../../roleaccess/utils';
import { normalizeAddressField } from '../../../utilities/normalizeAddressField';
import * as actions from '../projectActions';

const formValueSelector = state => _.get(state, 'form.projectEdit.values');

export default function* editProjectWatcher() {
  yield takeLatest(actions.EDIT, editProjectWorker);
}

export function* editProjectWorker(action) {
  yield put(actions.loading(true));
  const formData = yield select(formValueSelector);
  const formValues = JSON.parse(JSON.stringify(formData));
  formValues.closing_end_ts = `${formValues.closing_end_ts}`;
  formValues.status_code = formValues.status_code_desc?.value;
  
  // Transform UI display label back to backend value
  formValues.status_code_desc = transformStatusToBackend(formValues.status_code_desc?.label);
  
  // Parse numeric fields with NaN validation
  const parsedStoreNbr = parseInt(formValues.store_nbr, 10);
  if (isNaN(parsedStoreNbr)) {
    throw new Error('Invalid store number: must be a numeric value');
  }
  formValues.store_nbr = parsedStoreNbr;
  formValues.closing_start_ts = `${moment(formValues.closing_start_ts).format(
    'YYYY-MM-DD',
  )}`;
  
  // Format desk_audit_closing_actual_date if provided, otherwise set to null
  if (formValues.desk_audit_closing_actual_date) {
    formValues.desk_audit_closing_actual_date = `${moment(formValues.desk_audit_closing_actual_date).format(
      'YYYY-MM-DD',
    )}`;
  } else {
    formValues.desk_audit_closing_actual_date = null;
  }
  
  // Handle address field - may be string or object from dropdown
  formValues.addr_line1 = normalizeAddressField(formValues.addr_line1);

  // Ensure project_entity_rec_id saves as null when cleared (not as 0 or empty string)
  if (!formValues.project_entity_rec_id) {
    formValues.project_entity_rec_id = null;
  }
  
  // Check if user is PL Admin
  const state = yield select();
  const userRole = selectUserRoles(state);
  const isPLAdmin = getMaxWeight(userRole) === plAdminWeight();
  
  // PL Admin users are not authorized to modify discretionary approval
  if (isPLAdmin) {
    // Remove both discretionary_approval and disc_approval_ind from payload for PL Admin
    delete formValues.discretionary_approval;
    delete formValues.disc_approval_ind;
  } else if (formValues.discretionary_approval !== undefined) {
    // For non-PL Admin users, transform discretionary_approval to disc_approval_ind
    formValues.disc_approval_ind = formValues.discretionary_approval;
    delete formValues.discretionary_approval;
  }
  
  try {
    const response = yield call(updateProject, formValues);
    yield call(success, response, action.history);
  } catch (error) {
    yield call(failure, error);
  }
}

export function* success(response, history) {
  const data = _.get(response, 'data.updateProject', {});
  if (data.Success === 'EntityRecIdExists') {
    yield put(actions.loading(false));
    yield put(stopSubmit('projectEdit', {
      project_entity_rec_id: data.Message,
    }));
    return;
  }
  if (data.Success == 'true') {
    yield put(
      openSnackbar({
        message: 'Project updated!',
        showCloseIcon: true,
      }),
    );
    yield put(actions.loading(false));
    const id = data.closingId;
    
    // Check if store migration occurred
    if (data.storeMigration) {
      yield put(
        migrationOpenModal({
          migrationData: data.storeMigration,
          migrationMessage: data.Message,
        })
      );
    }
    
    yield call(history.push, `/projects/${id}`);
    yield put(destroy('projectEdit'));
  } else {
    // Check if store change was blocked due to documents linked to multiple projects
    // Support both new blockedDocuments format and legacy linkedDocuments format
    if (data.blockedDocuments && data.blockedDocuments.length > 0) {
      yield put(actions.loading(false));
      yield put(
        migrationOpenModal({
          blockedDocuments: data.blockedDocuments,
          migrationMessage: data.Message,
        })
      );
    } else if (data.linkedDocuments && data.linkedDocuments.length > 0) {
      // Handle legacy API response format
      yield put(actions.loading(false));
      yield put(
        migrationOpenModal({
          linkedDocuments: data.linkedDocuments,
          migrationMessage: data.Message,
        })
      );
    } else {
      yield call(failure, new Error(data.Message || 'Something went wrong while trying to update project'));
    }
  }
}

export function* failure(error) {
  yield put(actions.loading(false));
  yield put(actions.error(error));
  yield put(
    errorOpenModal({
      title: 'Project could not be updated',
      message: error.message,
    }),
  );
  return error;
}
